/**
 * API Client
 *
 * Axios instance configured for the M360Web API (NestJS backend).
 * As of 2026-01, all endpoints use NestJS with camelCase fields.
 */

import axios from 'axios';

// In development, use Vite proxy (empty string to use relative paths)
// In production, use relative paths since frontend and API are served from the same domain
const API_URL = import.meta.env.VITE_API_URL || '';

export const apiClient = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true, // Send cookies with requests
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Fallback auth for Mimecast/email-security redirect scenarios where httpOnly cookies
    // may not be sent. Uses sessionStorage (not localStorage) to limit XSS exposure.
    // Safe: apiClient has baseURL set to our API, so all requests go to same origin.
    const token = sessionStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Remove Content-Type for FormData to let axios set the correct multipart boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor — 401 retry is handled by the token lifecycle layer
// (see runtime/tokenLifecycle.ts). This interceptor only handles the fallback
// case where no refresh token is available (legacy sessions).
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const isAuthMeRequest = error.config?.url?.includes('/auth/me');
      const isRefreshRequest = error.config?.url?.includes('/auth/refresh');
      const isLoginPage = window.location.pathname === '/';
      const hasBeenRetried = error.config?._refreshRetried === true;

      if (!isAuthMeRequest && !isRefreshRequest && !isLoginPage && !hasBeenRetried) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);
